import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ExpertiseHeaderProps } from '../../types/expertise-detail.types';
import { formatExpertiseNumber } from '../../utils/formatters';
import { Button } from '../../../../../../ui/components/atoms/Button';

export const ExpertiseHeader: React.FC<ExpertiseHeaderProps> = ({
  expertise,
  onBack,
  backUrl = '/dashboard',
  isAdminView = false,
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(backUrl);
    }
  };

  const handleFileMaker = () => {
    const fmUrl = `fmp://$/fm2.chatelainxpert.be/ChatelainXpert.fmp12?script=mondaytofm&param=${expertise.affaire_id || expertise.id}`;
    window.location.href = fmUrl;
  };

  const handleOpenTracker = () => {
    const isAdminPath = window.location.pathname.includes('/admin');
    const basePath = isAdminPath ? '/admin' : '/bureau';
    navigate(`${basePath}/tracker/${expertise.affaire_id || expertise.id}`, {
      state: {
        from: window.location.pathname,
        fromExpertiseDetail: true
      }
    });
  };

  const getBackButtonText = () => {
    if (isAdminView) {
      return 'Retour à la vue admin';
    }
    if (backUrl.includes('dashboard')) {
      return 'Retour au tableau de bord';
    }
    if (backUrl.includes('expertises')) {
      return 'Retour aux expertises';
    }
    return 'Retour';
  };

  return (
    <div className="expertise-header">
      <div className="header-top-bar">
        <Button
          variant="outline-dark"
          size="small"
          onClick={handleBack}
          aria-label={getBackButtonText()}
          icon={
            <svg 
              width="18" 
              height="18" 
              viewBox="0 0 20 20" 
              fill="none"
            >
              <path 
                d="M12.5 5L7.5 10L12.5 15" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </svg>
          }
        >
          {getBackButtonText()}
        </Button>

        <div className="header-title-group">
          <h1 className="header-title">{expertise.type}</h1>
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <Button
            variant="secondary"
            size="small"
            onClick={handleOpenTracker}
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
              </svg>
            }
          >
            Tracker
          </Button>
          <Button
            variant="secondary"
            size="small"
            onClick={handleFileMaker}
            icon={
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="7" width="18" height="14" rx="2" ry="2"/>
                <rect x="8" y="7" width="8" height="5"/>
                <line x1="4" y1="17" x2="20" y2="17"/>
                <line x1="8" y1="17" x2="8" y2="21"/>
                <line x1="12" y1="17" x2="12" y2="21"/>
                <line x1="16" y1="17" x2="16" y2="21"/>
              </svg>
            }
          >
            FM
          </Button>
          <span className="expertise-number">
            {formatExpertiseNumber(expertise.number)}
          </span>
        </div>
      </div>

      <style>{`
        .expertise-header {
          margin-bottom: 28px;
          padding: 24px;
          background: #FFFFFF;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          border: 1px solid #E5E7EB;
          position: relative;
        }

        /* Top bar with back button and title */
        .header-top-bar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
          position: relative;
          z-index: 2;
        }

        /* Back button uses the Button component styles */

        .header-title-group {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .header-title {
          font-size: 28px;
          font-weight: 600;
          color: #002035;
          margin: 0;
          line-height: 1.2;
          letter-spacing: -0.5px;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .expertise-number {
          display: inline-flex;
          align-items: center;
          padding: 6px 14px;
          background: #FEF2F2;
          border: 1px solid #ef5343;
          border-radius: 20px;
          font-size: 13px;
          font-weight: 600;
          color: #ef5343;
          white-space: nowrap;
        }


        .meta-grid {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .meta-item {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 14px;
          background: #F8F9FA;
          border: 1px solid transparent;
          border-radius: 12px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .meta-item:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
          border-color: #CBD5E1;
        }

        .meta-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          flex-shrink: 0;
        }

        .meta-content {
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .meta-label {
          color: #6B7280;
          font-size: 11px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .meta-value {
          color: #002035;
          font-weight: 700;
          font-size: 14px;
          white-space: nowrap;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .meta-value.price {
          color: #10B981;
          font-weight: 700;
        }

        /* Tablet responsive */
        @media (max-width: 1024px) {
          .expertise-header {
            padding: 18px;
          }
          
          .header-top-bar {
            grid-template-columns: 1fr;
            gap: 16px;
          }

          .header-title-group {
            justify-content: flex-start;
            order: 2;
          }

          .meta-grid {
            width: 100%;
            justify-content: flex-start;
            order: 3;
          }

          .header-title {
            font-size: 24px;
          }
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .expertise-header {
            padding: 16px;
            border-radius: 12px;
            margin-bottom: 20px;
          }

          .header-top-bar {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          /* Mobile button adjustments */
          .header-top-bar .chatapp-button--small {
            padding: 6px 10px;
          }
          
          .header-top-bar .chatapp-button__text {
            display: none;
          }
          
          @media (min-width: 769px) {
            .header-top-bar .chatapp-button__text {
              display: inline;
            }
          }

          .header-title-group {
            justify-content: flex-start;
            order: 2;
          }

          .header-title {
            font-size: 22px;
          }

          .expertise-number {
            font-size: 11px;
            padding: 4px 10px;
          }


          .meta-grid {
            flex-direction: column;
            gap: 10px;
          }

          .meta-item {
            padding: 8px 12px;
          }

          .meta-value {
            font-size: 12px;
          }
        }

        /* Small mobile responsive */
        @media (max-width: 480px) {
          .expertise-header {
            padding: 14px;
            margin-bottom: 16px;
          }

          .header-title {
            font-size: 20px;
          }

          .header-title-group {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .expertise-number {
            align-self: flex-start;
          }

          .meta-grid {
            gap: 8px;
          }

          .meta-item {
            padding: 8px 10px;
            border-radius: 8px;
          }

          .meta-icon {
            font-size: 14px;
          }

          .meta-label {
            font-size: 9px;
          }

          .meta-value {
            font-size: 11px;
          }
        }
      `}</style>
    </div>
  );
};